<template>
  <div class="activity-job-container">
    <div class="search-con">
      <Input
        v-model="searchParams.job_name"
        placeholder="请输入职位名称"
        style="width: 300px; margin-right: 10px"
        clearable
        @on-enter="handleSearch"
      >
        <span slot="prepend">职位名称</span>
      </Input>

      <Select
        v-model="searchParams.education"
        placeholder="请选择学历要求"
        style="width: 150px; margin-right: 10px"
        clearable
      >
        <Option value="小学">小学</Option>
        <Option value="初中">初中</Option>
        <Option value="高中">高中</Option>
        <Option value="大专">大专</Option>
        <Option value="本科">本科</Option>
        <Option value="硕士">硕士</Option>
        <Option value="博士">博士</Option>
      </Select>

      <InputNumber
        v-model="searchParams.salary_min"
        :min="0"
        :max="100000"
        placeholder="最低工资"
        style="width: 120px; margin-right: 10px"
        clearable
      />

      <span style="margin-right: 10px">~</span>

      <InputNumber
        v-model="searchParams.salary_max"
        :min="0"
        :max="100000"
        placeholder="最高工资"
        style="width: 120px; margin-right: 10px"
        clearable
      />

      <Button type="primary" @click="handleSearch">搜索</Button>
      <Button type="success" @click="handleAdd" style="margin-left: 10px">添加职位</Button>
    </div>

    <!-- 职位列表 -->
    <div style="margin-top: 20px">
      <Table
        :columns="columns"
        :data="tableData"
        :loading="loading"
        border
        stripe
        @on-sort-change="handleSortChange"
      >
        <template slot-scope="{ row }" slot="action">
          <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
          <Button
            type="error"
            size="small"
            style="margin-left: 10px"
            @click="handleDelete(row)"
          >删除</Button>
        </template>

        <template slot-scope="{ row }" slot="salary">
          {{ formatSalary(row.salary_min, row.salary_max) }}
        </template>
      </Table>

      <!-- 分页 -->
      <div style="margin-top: 20px; text-align: right">
        <Page
          :total="total"
          :current="pageParams.page"
          :page-size="pageParams.limit"
          @on-change="handlePageChange"
          @on-page-size-change="handlePageSizeChange"
          show-sizer
          show-total
        />
      </div>
    </div>

    <!-- 添加/编辑职位弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="700px"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="jobForm" :model="formData" :rules="formRules">
        <FormItem label="职位名称" prop="job_name">
          <Input v-model="formData.job_name" placeholder="请输入职位名称" />
        </FormItem>

        <FormItem label="职位描述" prop="job_desc">
          <Input
            v-model="formData.job_desc"
            type="textarea"
            :rows="4"
            placeholder="请输入职位描述"
          />
        </FormItem>

        <FormItem label="薪资范围">
          <InputNumber
            v-model="formData.salary_min"
            :min="0"
            :max="100000"
            placeholder="最低工资"
            style="width: 150px; margin-right: 10px"
          />
          <span>~</span>
          <InputNumber
            v-model="formData.salary_max"
            :min="0"
            :max="100000"
            placeholder="最高工资"
            style="width: 150px; margin-left: 10px"
          />
          <span style="margin-left: 10px">元/月</span>
        </FormItem>

        <FormItem label="学历要求" prop="education">
          <Select v-model="formData.education">
            <Option value="小学">小学</Option>
            <Option value="初中">初中</Option>
            <Option value="高中">高中</Option>
            <Option value="大专">大专</Option>
            <Option value="本科">本科</Option>
            <Option value="硕士">硕士</Option>
            <Option value="博士">博士</Option>
          </Select>
        </FormItem>

        <FormItem label="经验要求" prop="experience">
          <Select v-model="formData.experience">
            <Option value="无经验">无经验</Option>
            <Option value="1年以下">1年以下</Option>
            <Option value="1-3年">1-3年</Option>
            <Option value="3-5年">3-5年</Option>
            <Option value="5-10年">5-10年</Option>
            <Option value="10年以上">10年以上</Option>
          </Select>
        </FormItem>

        <FormItem label="招聘人数" prop="recruitment_number">
          <InputNumber
            v-model="formData.recruitment_number"
            :min="1"
            :max="1000"
            placeholder="招聘人数"
            style="width: 150px"
          />
          <span style="margin-left: 10px">人</span>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit">确定</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getCompanyJobList,
  addCompanyJob,
  updateCompanyJob,
  deleteCompanyJob
} from '@/api/activity.js'

export default {
  name: 'ActivityJobManagement',
  props: {
    activityId: {
      type: [Number, String],
      required: true
    },
    companyId: {
      type: [Number, String],
      required: true
    }
  },
  created () {
    console.log('ActivityJobManagement created')
    console.log('Props in created:', {
      activityId: this.activityId,
      companyId: this.companyId
    })
  },
  watch: {
    // 监听 companyId 变化，当点击查看职位时重新获取数据
    companyId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          console.log('companyId 发生变化，重新获取职位数据:', newVal)
          this.getCompanyJobList()
        }
      }
    }
  },
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,

      searchParams: {
        job_name: '',
        education: '',
        salary_min: null,
        salary_max: null
      },

      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      formData: {
        id: null,
        activity_id: null,
        company_id: null,
        job_name: '',
        job_desc: '',
        salary_min: 0,
        salary_max: 0,
        education: '大专',
        experience: '1-3年',
        recruitment_number: 1
      },

      formRules: {
        job_name: [
          { required: true, message: '请输入职位名称', trigger: 'blur' },
          { min: 2, max: 100, message: '职位名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        job_desc: [
          { required: true, message: '请输入职位描述', trigger: 'blur' },
          { min: 10, max: 2000, message: '职位描述长度在 10 到 2000 个字符', trigger: 'blur' }
        ],
        salary_min: [
          { required: true, message: '请输入最低工资', trigger: 'blur' },
          { type: 'number', min: 0, message: '最低工资不能小于 0', trigger: 'blur' }
        ],
        salary_max: [
          { required: true, message: '请输入最高工资', trigger: 'blur' },
          { type: 'number', min: 0, message: '最高工资不能小于 0', trigger: 'blur' }
        ],
        recruitment_number: [
          { required: true, message: '请输入招聘人数', trigger: 'blur' },
          { type: 'number', min: 1, message: '招聘人数至少 1 人', trigger: 'blur' }
        ]
      },

      columns: [
        { title: 'ID', key: 'id', width: 80, sortable: true },
        { title: '职位名称', key: 'job_name', width: 200, sortable: true, tooltip: true },
        { title: '薪资范围', key: 'Jsalary_CN', width: 150 , tooltip: true},
        { title: '学历要求', key: 'Jeducation_CN', width: 100 , tooltip: true},
        { title: '经验要求', key: 'Jexperience_CN', width: 100 , tooltip: true},
        { title: '招聘人数', key: 'recruitment_number', width: 100 , tooltip: true},
        { title: '操作', slot: 'action', width: 150, fixed: 'right' }
      ]
    }
  },

  mounted () {
    console.log('ActivityJobManagement mounted')
    this.formData.activity_id = this.activityId
    this.formData.company_id = this.companyId
  },

  methods: {
    // 获取职位列表
    async getCompanyJobList () {
      console.log('开始获取职位列表')
      console.log('参数:', { activityId: this.activityId, companyId: this.companyId })

      this.loading = true
      try {
        // 准备参数，确保数值类型正确
        const params = {
          ...this.searchParams,
          salary_min: this.searchParams.salary_min || '',
          salary_max: this.searchParams.salary_max || '',
          page: this.pageParams.page,
          limit: this.pageParams.limit
        }

        console.log('发送API请求:', params)

        const res = await getCompanyJobList(this.activityId, this.companyId, params)

        console.log('API响应:', res)

        if (res.data.code === 0) {
          this.tableData = res.data.data.list || []
          this.total = res.data.data.total || 0
          console.log('职位列表:', this.tableData)
        } else {
          this.$Message.error(res.data.msg || '获取职位列表失败')
        }
      } catch (error) {
        console.error('获取职位列表异常:', error)
        this.$Message.error('获取职位列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch () {
      this.pageParams.page = 1
      this.getCompanyJobList()
    },

    // 排序
    handleSortChange ({ column, key, order }) {
      if (order === 'asc') {
        this.tableData.sort((a, b) => a[key] > b[key] ? 1 : -1)
      } else if (order === 'desc') {
        this.tableData.sort((a, b) => a[key] < b[key] ? 1 : -1)
      }
    },

    // 分页
    handlePageChange (page) {
      this.pageParams.page = page
      this.getCompanyJobList()
    },

    handlePageSizeChange (pageSize) {
      this.pageParams.limit = pageSize
      this.pageParams.page = 1
      this.getCompanyJobList()
    },

    // 添加职位
    handleAdd () {
      this.modalTitle = '添加职位'
      this.formData = {
        id: null,
        activity_id: this.activityId,
        company_id: this.companyId,
        job_name: '',
        job_desc: '',
        salary_min: 0,
        salary_max: 0,
        education: '大专',
        experience: '1-3年',
        recruitment_number: 1
      }
      this.modalVisible = true
    },

    // 编辑职位
    handleEdit (row) {
      this.modalTitle = '编辑职位'
      this.formData = { ...row }
      this.modalVisible = true
    },

    // 删除职位
    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除该职位吗？删除后相关数据将无法恢复。',
        onOk: async () => {
          try {
            const res = await deleteCompanyJob(row.id)
            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getCompanyJobList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    // 格式化薪资
    formatSalary (min, max) {
      if (min === 0 && max === 0) {
        return '面议'
      }
      return `${min.toLocaleString()}~${max.toLocaleString()}元/月`
    },

    // 提交表单
    async handleSubmit () {
      try {
        this.$refs.jobForm.validate(async (valid) => {
          if (valid) {
            let res
            if (this.formData.id) {
              res = await updateCompanyJob(this.formData)
            } else {
              res = await addCompanyJob(this.formData)
            }

            if (res.data.code === 0) {
              this.$Message.success(this.formData.id ? '更新成功' : '添加成功')
              this.modalVisible = false
              this.getCompanyJobList()
              // 通知父组件更新
              this.$emit('on-update')
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.activity-job-container {
  padding: 10px;
}

.search-con {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
</style>
